import * as React from "react";
import { AppRoutes } from "./routes/Routes";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./store";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { enUS, trTR, arSD, jaJP } from "@mui/material/locale";
import createMuiTheme from "./config/createTheme";
import { useTranslation } from "react-i18next";
import QueryProvider from "./components/providers/QueryProvider";
import { applyKendoTheme, DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME } from "./config/kendoThemes";

const MUI_LOCALE_MAP = { tr: trTR, en: enUS, ar: arSD, ja: jaJP };

function AppInner() {
  const { i18n } = useTranslation();
  const mode = useSelector((x) => x.theme?.mode ?? "dark");
  const kendoTheme = useSelector((x) => x.theme?.kendoTheme ?? null);
  const lang = i18n.language?.split("-")[0] ?? "tr";
  const isRTL = lang === "ar";
  const currentLocale = MUI_LOCALE_MAP[lang] ?? enUS;
  const theme = createMuiTheme(currentLocale, mode, isRTL ? "rtl" : "ltr");

  // Sync data-theme for CSS custom properties (layout effect to avoid flash)
  React.useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  // Kendo tema senkronizasyonu
  // kendoTheme null ise → app mode ile otomatik eşleşir
  // kendoTheme set ise → manuel seçim (mode değişse bile değişmez)
  React.useLayoutEffect(() => {
    const effective = kendoTheme ?? (mode === "dark" ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME);
    applyKendoTheme(effective);
  }, [mode, kendoTheme]);

  // Sync dir for RTL/LTR
  React.useLayoutEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryProvider>
        <AppRoutes />
        <ToastContainer
          position={isRTL ? "top-left" : "top-right"}
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={isRTL}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode === "dark" ? "dark" : "light"}
        />
      </QueryProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Provider store={store}>
        <AppInner />
      </Provider>
    </BrowserRouter>
  );
}

export default App;
